import { makeStyles, tokens } from '@fluentui/react-components';

export const useEvalRunsPageStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    padding: '24px',
    gap: '20px',
    minWidth: 0,
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    flexWrap: 'wrap',
  },
  toolbarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
    flexGrow: 1,
    minWidth: 0,
  },
  toolbarRight: { display: 'flex', alignItems: 'center', gap: '8px' },
  searchInput: { minWidth: 0, flexGrow: 1, flexBasis: '220px' },
  filtersExpanded: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground2,
    ':hover': { backgroundColor: tokens.colorBrandBackground2Hover },
    ':active': { backgroundColor: tokens.colorBrandBackground2Pressed },
  },
  filterRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    padding: '12px',
    alignItems: 'center',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  filterField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    fontSize: tokens.fontSizeBase200,
    minWidth: 0,
    flexShrink: 1,
    width: 'max-content',
    maxWidth: '100%',
  },
  contextCombobox: { minWidth: 0, width: '100%' },
  contextInput: { minWidth: 0, width: '100%' },
  filterPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '0 0 12px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  filterGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    gap: '12px',
  },
  activeFilters: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  filterChipText: {
    maxWidth: '240px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  contextSuggestions: {
    width: 'max-content',
    maxWidth: 'min(420px, calc(100vw - 32px))',
    maxHeight: 'min(240px, calc(100vh - 80px))',
    overflowY: 'auto',
    overflowWrap: 'anywhere',
  },
  suggestionText: {
    display: 'block',
    marginBottom: '12px',
    minWidth: 0,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  list: {
    containerType: 'inline-size',
    containerName: 'eval-runs',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    overflowX: 'auto',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  listHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '12px 16px',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  loadedRange: {
    padding: '0 16px 12px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    lineHeight: '20px',
  },
  table: { tableLayout: 'fixed', width: '100%' },
  tableHeader: {
    backgroundColor: tokens.colorBrandBackground2,
    '& th': {
      color: tokens.colorNeutralForeground2,
      fontWeight: tokens.fontWeightSemibold,
    },
  },
  nameColumn: {
    paddingLeft: '16px',
    width: '27%',
    '@container eval-runs (max-width: 1000px)': { width: 'auto' },
  },
  dateColumn: {
    width: '17%',
    '@container eval-runs (max-width: 1000px)': { width: '140px' },
    '@container eval-runs (max-width: 480px)': { width: '108px' },
  },
  statusColumn: { width: '140px' },
  pipelineColumn: {
    '@container eval-runs (max-width: 1000px)': { display: 'none' },
  },
  creatorColumn: {
    '@container eval-runs (max-width: 1000px)': { display: 'none' },
  },
  branchColumn: {
    '@container eval-runs (max-width: 1000px)': { width: '160px' },
    '@container eval-runs (max-width: 640px)': { display: 'none' },
  },
  compactHistoryPipeline: {
    display: 'none',
    '@container eval-runs (max-width: 1000px)': {
      display: 'block',
      overflowWrap: 'anywhere',
      color: tokens.colorNeutralForeground2,
    },
  },
  cell: {
    padding: '12px 16px',
    overflowWrap: 'anywhere',
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground2,
    '@container eval-runs (max-width: 640px)': { padding: '12px 8px' },
  },
  row: {
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  rowName: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorBrandForegroundLink,
    display: 'block',
    textAlign: 'left',
    padding: 0,
    minHeight: '20px',
    minWidth: 0,
    maxWidth: '100%',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    lineHeight: '20px',
    ':hover': { color: tokens.colorBrandForegroundLink },
  },
  primaryText: {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground2,
  },
  pipelineMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    minWidth: 0,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightRegular,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    '& > *': {
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  pipelineIcon: {
    color: tokens.colorPaletteTealForeground2,
    flexShrink: 0,
    fontSize: '16px',
  },
  overviewDate: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground2,
  },
  rowMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginTop: '2px',
    lineHeight: '16px',
  },
  historyToggle: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorBrandForeground2,
    minHeight: '24px',
    padding: '0 4px',
  },
  historyCell: {
    padding: '20px 24px',
    backgroundColor: tokens.colorNeutralBackground2,
    '@container eval-runs (max-width: 640px)': { padding: '12px 8px' },
  },
  historyPanel: { maxWidth: '780px' },
  historyCaption: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '24px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    paddingBottom: '16px',
    fontWeight: tokens.fontWeightSemibold,
  },
  historyList: {
    listStyleType: 'none',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    overflow: 'hidden',
    '& > li + li': {
      borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    padding: 0,
    margin: 0,
  },
  historyRun: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 200px) auto',
    alignItems: 'center',
    gap: '16px',
    width: '100%',
    minWidth: 0,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    textAlign: 'left',
    padding: '12px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: 0,
    whiteSpace: 'normal',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    '@media (max-width: 700px)': {
      gridTemplateColumns: 'minmax(0, 1fr) auto',
      gap: '8px',
    },
    '@container eval-runs (max-width: 640px)': {
      gridTemplateColumns: 'minmax(0, 1fr) auto',
      gap: '8px',
      padding: '12px 8px',
    },
  },
  historyRunSharedCreator: {
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    '@media (max-width: 700px)': {
      gridTemplateColumns: 'minmax(0, 1fr) auto',
    },
  },
  historyCreator: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0,
    '@media (max-width: 700px)': {
      gridColumn: '1',
      gridRow: '2',
    },
    '@container eval-runs (max-width: 640px)': {
      gridColumn: '1',
      gridRow: '2',
    },
  },
  historyAction: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '8px',
    whiteSpace: 'nowrap',
    '@media (max-width: 700px)': {
      gridColumn: '2',
      gridRow: '1',
    },
    '@container eval-runs (max-width: 640px)': {
      gridColumn: '2',
      gridRow: '1',
    },
  },
  historyReport: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    color: tokens.colorBrandForegroundLink,
    fontWeight: tokens.fontWeightSemibold,
  },
  historyReportIcon: { fontSize: '16px' },
  historySummary: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: 0,
  },
  historyDate: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground2,
  },
  historyMore: { marginTop: '12px' },
  historyContext: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '8px',
    color: tokens.colorNeutralForeground3,
    minWidth: 0,
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
  },
  historyBranch: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  historyBranchIcon: {
    flexShrink: 0,
    color: tokens.colorBrandForeground2,
  },
  historyBuild: {
    color: tokens.colorNeutralForeground3,
    '::before': { content: '"·"', marginRight: '8px' },
  },
  loadMore: { alignSelf: 'center' },
  statusBadge: {
    justifyContent: 'center',
    boxSizing: 'border-box',
    width: '108px',
    height: '28px',
  },
  statusIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    lineHeight: 0,
    '& svg': { display: 'block' },
  },
  statusIconSpinning: {
    animationName: {
      from: { transform: 'rotate(0deg)' },
      to: { transform: 'rotate(360deg)' },
    },
    animationDuration: '1.4s',
    animationTimingFunction: 'linear',
    animationIterationCount: 'infinite',
    '@media (prefers-reduced-motion: reduce)': { animationName: 'none' },
  },
  branch: {
    maxWidth: '100%',
    minWidth: 0,
    height: '24px',
    '& .fui-Badge__icon': { flexShrink: 0 },
  },
  branchText: {
    minWidth: 0,
    whiteSpace: 'nowrap',
    direction: 'rtl',
    textAlign: 'left',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontFamily: tokens.fontFamilyBase,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorBrandForeground2,
    fontSize: tokens.fontSizeBase200,
  },
  emptyState: {
    padding: '48px',
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
  },
});
